import { useEffect, useState, useCallback} from 'react';
import { useAuth0, getSelectedTenantId } from '../app/auth';
import { dailyPublishedBudgetFormatter } from './reportsHelper';

import { publicationProxy } from '../common/apiProxy';
import {costCenterProxy} from '../common/proxies/negServiceProxy';
import { transformObject } from '../common/formatter';

const useBudgetQuery = () =>{

    const { getTokenSilently, user } = useAuth0();
    const [tenantId, setTenantId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [tenantBudgets, setTenantBudgets] = useState(null);
    const [budget, setBudget] = useState(null);
    const [allocatedBudget, setAllocatedBudget] = useState(null);
    const [publishedBudget, setPublishedBudget] = useState(null);
    const [dailyPublishedBudget, setDailyPublishedBudget] = useState(null);
    const currentBudget = tenantBudgets && transformObject(tenantBudgets.find(item => item.id === budget));
    const [weeklyAllocations, setWeeklyAllocations] = useState(null);
    const [weeklyForecast, setWeeklyForecast] = useState(null);
    const [monthlyForecast, setMonthlyForecast] = useState(null);
    const [forecastSummary, setForecastSummary] = useState(null);

    const getBudgets = useCallback( async () => {
        const accessToken = await getTokenSilently();
        if (!tenantId) return setIsLoading(false);
        const budgetsInfo = await costCenterProxy.getBudgetsByTenantId(accessToken, tenantId);
        setTenantBudgets(budgetsInfo);
        if(budgetsInfo && budgetsInfo.length === 1){
            const budget = budgetsInfo[0];
            setBudget(budget.id);
        }
        return setIsLoading(false);
    }, [getTokenSilently, tenantId ]);

    useEffect( () => {
        (async () => {
            const accessToken = await getTokenSilently();
            setTenantId(getSelectedTenantId(user, accessToken));
        })();
    },[user, getTokenSilently]);

    useEffect( () =>{
        getBudgets();
    }, [getBudgets]);


    const getPublishedBudget = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setPublishedBudget(null);
        const publishedBudgetData = await publicationProxy.getPublicationsBudget('published', budget, accessToken, tenantId);
        setPublishedBudget(publishedBudgetData);
        return setIsLoading(false);
    }, [getTokenSilently, tenantId, budget ]);

    const getAllocatedBudget = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setAllocatedBudget(null);
        const allocatedBudgetData = await publicationProxy.getPublicationsBudget('allocated', budget, accessToken, tenantId);
        setAllocatedBudget(allocatedBudgetData);
        return setIsLoading(false);
    }, [getTokenSilently, tenantId, budget ]);

    const getDailyPublishedBudget = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setDailyPublishedBudget(null);
        const dailyPublishedBudgetList = await publicationProxy.getDailyPublishedBudget(budget, accessToken);
        const formatedDailyPublishedBudget = dailyPublishedBudgetFormatter(dailyPublishedBudgetList);
        setDailyPublishedBudget(formatedDailyPublishedBudget);
        return setIsLoading(false);

    }, [getTokenSilently, budget ]);

    const getWeeklyAllocations = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setWeeklyAllocations(null);
        const weeklyAllocations = await publicationProxy.getWeeklyAllocationBudgets(budget, accessToken);
        setWeeklyAllocations(weeklyAllocations);
        return setIsLoading(false);
    }, [getTokenSilently, budget]);

    const getBudgetForecast = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setWeeklyForecast(null);
        setMonthlyForecast(null);
        const budgetForecast = await publicationProxy.getBudgetForecast(budget, accessToken);
        if (budgetForecast) {
            setWeeklyForecast(budgetForecast.weekly);
            setMonthlyForecast(budgetForecast.monthly);
        }
        return setIsLoading(false);
    }, [getTokenSilently, budget]);

    const getBudgetForecastSummary = useCallback( async () => {
        if (!budget) return setIsLoading(false);
        const accessToken = await getTokenSilently();
        setIsLoading(true);
        setForecastSummary(null);
        const summary = await publicationProxy.getBudgetForecastSummary(budget, accessToken);
        if (summary) {
            setForecastSummary(summary);
        }
        return setIsLoading(false);
    }, [getTokenSilently, budget]);

    useEffect( () =>{
        getAllocatedBudget();
        getPublishedBudget();
        getDailyPublishedBudget();
        getWeeklyAllocations();
        getBudgetForecast();
        getBudgetForecastSummary();
    }, [getAllocatedBudget,  getPublishedBudget, getDailyPublishedBudget, 
        getWeeklyAllocations, getBudgetForecast, getBudgetForecastSummary, budget]);

    return {
        tenantBudgets,
        setBudget,
        publishedBudget,
        allocatedBudget,
        dailyPublishedBudget,
        isLoading,
        currentBudget,
        weeklyAllocations,
        weeklyForecast,
        monthlyForecast,
        forecastSummary
      };
};

export default useBudgetQuery;
